import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Brain, AlertTriangle, CheckCircle2, Sparkles, TrendingUp,
    ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const URGENCY_STYLES = {
    urgent: { label: 'Urgent', tone: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
    semi_urgent: { label: 'Semi-urgent', tone: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
    routine: { label: 'Routine', tone: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
    screening_rejected: { label: 'Below threshold', tone: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
};

function UrgencyChip({ level }) {
    const cfg = URGENCY_STYLES[level] || URGENCY_STYLES.routine;
    return (
        <Badge variant="outline" className={`gap-1.5 ${cfg.tone}`}>
            <span className={`inline-block w-2 h-2 rounded-full ${cfg.dot}`} />
            {cfg.label}
        </Badge>
    );
}

/**
 * Surfaces the urgency_ml_service prediction inside the Consultant Review panel.
 * - Auto-loads on mount.
 * - Shows the suggested urgency, confidence bar and top reasons.
 * - "Apply suggestion" prefills the urgency-override <Select> in the parent.
 * - Shows agreement / disagreement state once the consultant locks in a decision.
 *
 * Authentication: assumes axios.defaults.headers.common.Authorization is set
 * globally by AuthContext (it is).
 */
export default function MLUrgencyPanel({
    referralId,
    currentUrgency,
    onApplySuggestion,
    overrideValue,
}) {
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const [prediction, setPrediction] = useState(null);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (!referralId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const [predRes, statsRes] = await Promise.all([
                    axios.get(`${API}/ml/urgency/predict/${referralId}`),
                    axios.get(`${API}/ml/stats`).catch(() => null),
                ]);
                if (cancelled) return;
                setPrediction(predRes.data?.ml_prediction || null);
                setStats(statsRes?.data || null);
            } catch (e) {
                if (cancelled) return;
                const detail = e?.response?.data?.detail;
                if (e?.response?.status === 503) {
                    setError('ML service is not available yet.');
                } else if (e?.response?.status === 403) {
                    setError('ML predictions are only available to consultants.');
                } else if (detail) {
                    setError(detail);
                } else {
                    setError('Unable to load ML prediction.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [referralId]);

    if (!prediction && !loading && !error) return null;

    const suggested = prediction?.predicted_urgency || prediction?.urgency;
    const confidence = Math.round((prediction?.confidence || 0) * 100);
    // Backend returns `reasoning` as a single string ("Predicted x based on: a; b; c").
    // Split on '; ' for nicer bullet rendering; also accept array-form for forward-compat.
    const reasons = (() => {
        const r = prediction?.reasons || prediction?.factors || prediction?.reasoning;
        if (Array.isArray(r)) return r;
        if (typeof r === 'string') {
            const colonIdx = r.indexOf('based on:');
            const body = colonIdx >= 0 ? r.slice(colonIdx + 'based on:'.length) : r;
            return body.split(';').map((s) => s.trim()).filter(Boolean);
        }
        return [];
    })();
    const trainingCount = stats?.training_count ?? prediction?.model_training_count ?? 0;
    const isTrained = stats?.status === 'trained' && trainingCount > 0;
    const isRuleBased = !isTrained;
    const agreementBadge = (() => {
        if (!overrideValue || !suggested) return null;
        if (overrideValue === suggested) {
            return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"><CheckCircle2 className="w-3 h-3"/>Agrees with ML</Badge>;
        }
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-1"><AlertTriangle className="w-3 h-3"/>Overrides ML</Badge>;
    })();

    return (
        <Card className="border-violet-200 bg-gradient-to-br from-violet-50/60 to-white" data-testid="ml-urgency-panel">
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-violet-600 text-white grid place-items-center">
                            <Brain className="w-4 h-4" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-slate-800">ML urgency suggestion</p>
                            <p className="text-xs text-slate-500">
                                {isRuleBased
                                    ? 'Rule-based stratification (model still learning).'
                                    : `Trained on ${trainingCount} consultant decisions.`}
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="ml-panel-toggle"
                    >
                        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                </div>

                {open && (
                    <div className="space-y-3">
                        {loading && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Computing prediction...
                            </div>
                        )}
                        {error && !loading && (
                            <p className="text-sm text-slate-500 italic">{error}</p>
                        )}
                        {prediction && !loading && (
                            <>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs text-slate-500">Suggests</span>
                                    <UrgencyChip level={suggested} />
                                    {currentUrgency && currentUrgency !== suggested && (
                                        <>
                                            <span className="text-xs text-slate-400">vs current</span>
                                            <UrgencyChip level={currentUrgency} />
                                        </>
                                    )}
                                    {agreementBadge}
                                </div>

                                <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500">Confidence</span>
                                        <span className="font-mono text-slate-700">{confidence}%</span>
                                    </div>
                                    <Progress value={confidence} className="h-1.5" data-testid="ml-confidence-bar" />
                                </div>

                                {reasons.length > 0 && (
                                    <div className="space-y-1">
                                        <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                                            <Sparkles className="w-3 h-3" /> Top reasons
                                        </p>
                                        <ul className="text-xs text-slate-600 space-y-1 ml-4 list-disc">
                                            {reasons.slice(0, 4).map((r, i) => (
                                                <li key={`reason-${i}-${(r || '').slice(0, 12)}`}>
                                                    {typeof r === 'string' ? r : r?.text || JSON.stringify(r)}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {suggested && (
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                onApplySuggestion?.(suggested);
                                                toast.success(`Applied ML suggestion: ${URGENCY_STYLES[suggested]?.label || suggested}`);
                                            }}
                                            disabled={overrideValue === suggested}
                                            data-testid="apply-ml-suggestion"
                                            className="border-violet-300 text-violet-700 hover:bg-violet-100"
                                        >
                                            <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
                                            {overrideValue === suggested ? 'Suggestion applied' : 'Apply suggestion'}
                                        </Button>
                                        <p className="text-[11px] text-slate-400 italic self-center">
                                            Suggestion is advisory only — clinical judgement overrides.
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
